/**
 * The thread's messages (boards M and O, „Wiadomości"): the trainer's bubble, the answers bundle,
 * Buddy's message with its progress, model text in its one formatting, and the disclosure line.
 */

import { Collapsible } from '@base-ui/react/collapsible';
import clsx from 'clsx';
import { Fragment, useState, type CSSProperties, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { messageSegments, type AnsweredQuestion, type TurnProgress } from '~/entities/ai-conversation';
import { PearlMark } from '~/shared/ui/PearlMark';

import { liveStepWords } from '../lib/planWording';

import { ChevronGlyph } from './ChatGlyphs';
import surfaces from './surfaces.module.css';
import styles from './Messages.module.css';

/**
 * `still`: the message was already there when the thread first drew (a resumed conversation), so it
 * stands without its entrance; only what arrives after the first paint rises or streams in.
 */
interface Still {
  readonly still?: boolean;
}

export function TrainerBubble({ text, still = false }: { readonly text: string } & Still) {
  return (
    <p className={`${String(styles.bubble)} ${String(surfaces.rise)}`} data-still={still ? '' : undefined}>
      {text}
    </p>
  );
}

/** The trainer's answers to the questions, as one message: each question over its answer. */
export function AnswersBubble({
  answers,
  ownWords,
  still = false,
}: { readonly answers: readonly AnsweredQuestion[]; readonly ownWords: string | null } & Still) {
  const { t } = useTranslation();
  return (
    <div
      className={`${String(styles.bubble)} ${String(styles.answers)} ${String(surfaces.rise)}`}
      data-still={still ? '' : undefined}
    >
      <dl className={styles.answerList}>
        {answers.map((answer) => (
          <Fragment key={answer.question}>
            <dt className={styles.question}>{answer.question}</dt>
            <dd className={styles.answer}>
              {answer.ownWords ? t('web:aiPlan.answers.ownWords', { text: answer.answer }) : answer.answer}
            </dd>
          </Fragment>
        ))}
      </dl>
      {/* Words the trainer wrote beside answers that were all picked: after them, in their own voice. */}
      {ownWords !== null && <p className={styles.answer}>{t('web:aiPlan.answers.ownWords', { text: ownWords })}</p>}
    </div>
  );
}

/**
 * Buddy's message (owner, 10 October 2026; P5d-3, 4 November 2026): flat, no bubble, the mark beside
 * it. While the turn is worked on the mark's light breathes beside the ONE line of the step it is
 * on; then the steps folded behind „Pokaż kroki", and whatever the turn produced, the mark still.
 */
export function BuddyMessage({
  progress,
  children,
  still = false,
}: { readonly progress: TurnProgress; readonly children: ReactNode } & Still) {
  const { t } = useTranslation();
  return (
    <div className={`${String(styles.buddy)} ${String(surfaces.rise)}`} data-still={still ? '' : undefined}>
      <PearlMark state={progress.live === null ? 'rest' : 'work'} className={styles.mark} />
      <div className={styles.body}>
        {progress.live !== null && (
          // Hidden from the reader: the composer's status is the one announcement of the step.
          <p className={clsx(styles.text, surfaces.shimmer)} aria-hidden="true">
            {liveStepWords(t, progress.live)}
          </p>
        )}
        {progress.steps.length > 0 && <StepsFold steps={progress.steps} />}
        {children}
      </div>
    </div>
  );
}

/** Every step Buddy reported on a turn, collapsed until the trainer opens it. Text only, no time. */
function StepsFold({ steps }: { readonly steps: readonly string[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <Collapsible.Root open={open} onOpenChange={setOpen} className={styles.steps}>
      <Collapsible.Trigger className={styles.stepsTrigger}>
        <ChevronGlyph className={styles.stepsChevron} />
        {t(open ? 'web:aiPlan.steps.hide' : 'web:aiPlan.steps.show')}
      </Collapsible.Trigger>
      <Collapsible.Panel>
        <ol className={styles.stepList}>
          {steps.map((step, index) => (
            // The log only grows, oldest first: a line's place is its identity.
            <li key={index}>{step}</li>
          ))}
        </ol>
      </Collapsible.Panel>
    </Collapsible.Root>
  );
}

/** Model text as plain text, `**bold**` honoured and nothing else (R33). */
export function ModelText({ text, className }: { readonly text: string; readonly className?: string }) {
  return (
    <p className={className ?? styles.text}>
      {messageSegments(text).map((segment, index) =>
        segment.strong ? <b key={index}>{segment.text}</b> : <Fragment key={index}>{segment.text}</Fragment>,
      )}
    </p>
  );
}

/**
 * Model text streamed in: each word clears from its blur a beat after the one before (board O,
 * „Buddy pisze"). The words are whole from the first frame, so nothing reflows as they appear.
 */
export function StreamedText({ text }: { readonly text: string }) {
  return (
    <p className={styles.text}>
      {streamTokens(text).map((token, index) => {
        if (token.order === null) return <Fragment key={index}>{token.text}</Fragment>;
        const word = (
          <span className={styles.word} style={{ '--word-index': token.order } as CSSProperties}>
            {token.text}
          </span>
        );
        return token.strong ? <b key={index}>{word}</b> : <Fragment key={index}>{word}</Fragment>;
      })}
    </p>
  );
}

interface StreamToken {
  readonly text: string;
  readonly strong: boolean;
  /** The word's place in the stream; `null` for the whitespace between words. */
  readonly order: number | null;
}

/** The text's words in reading order, each with its place in the stream, bold kept per word. */
function streamTokens(text: string): StreamToken[] {
  const parts = messageSegments(text).flatMap((segment) =>
    segment.text.split(/(\s+)/).filter((part) => part !== '').map((part) => ({ text: part, strong: segment.strong })),
  );
  let order = 0;
  return parts.map((part) => {
    if (part.text.trim() === '') return { ...part, order: null };
    order += 1;
    return { ...part, order: order - 1 };
  });
}

/** Above the first message (R49): the trainer is talking to an AI, named Buddy, whose output is checked. */
export function Disclosure() {
  return (
    <p className={styles.disclosure}>
      <PearlMark className={styles.disclosureMark} />
      <span>
        <Trans i18nKey="web:aiPlan.disclosure" components={{ b: <b /> }} />
      </span>
    </p>
  );
}
